import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type { Character, CharacterType } from '@/types/character';
import { blankCharacter } from '@/types/character';
import {
  scheduleLibrarySave,
  loadLibrary,
  genId,
  type LibraryEntry,
} from '@/lib/storage';

interface Store {
  // Library state
  characters: LibraryEntry[];
  activeId: string | null;
  // Derived: the currently-active character, kept in sync by every action.
  character: Character | null;

  // Character lifecycle
  newCharacter: (type: CharacterType) => Character;
  setCharacter: (c: Character) => void;
  updateCharacter: (patch: Partial<Character> | ((c: Character) => Character)) => void;
  addCharacter: (c: Character) => string;
  removeCharacter: (id: string) => void;
  activateCharacter: (id: string) => void;
  duplicateCharacter: (id: string) => string | null;
  reset: () => void;
}

function activeFrom(entries: LibraryEntry[], id: string | null): Character | null {
  if (!id) return null;
  return entries.find((e) => e.id === id)?.char ?? null;
}

const initialLib = loadLibrary();

export const useStore = create<Store>()(
  subscribeWithSelector((set, get) => ({
    characters: initialLib.characters,
    activeId: initialLib.activeId,
    character: activeFrom(initialLib.characters, initialLib.activeId),

    newCharacter: (type) => {
      const c = blankCharacter(type);
      const id = genId();
      set((s) => {
        const characters = [...s.characters, { id, char: c }];
        return { characters, activeId: id, character: c };
      });
      return c;
    },

    // Replace (or create) the active character. Used by import flows where the
    // intent is "this IS my character now" — NOT by the library-paste flow,
    // which uses addCharacter to preserve existing work.
    setCharacter: (c) => {
      set((s) => {
        const hasActive = s.activeId && s.characters.some((e) => e.id === s.activeId);
        if (hasActive) {
          const characters = s.characters.map((e) =>
            e.id === s.activeId ? { ...e, char: c } : e
          );
          return { characters, activeId: s.activeId, character: c };
        }
        const id = genId();
        return {
          characters: [...s.characters, { id, char: c }],
          activeId: id,
          character: c,
        };
      });
    },

    updateCharacter: (patch) => {
      const cur = get().character;
      const activeId = get().activeId;
      if (!cur || !activeId) return;
      const next: Character =
        typeof patch === 'function'
          ? (patch as (c: Character) => Character)(cur)
          : { ...cur, ...patch };
      next.updatedAt = new Date().toISOString();
      set((s) => {
        const characters = s.characters.map((e) =>
          e.id === activeId ? { ...e, char: next } : e
        );
        return { characters, character: next };
      });
    },

    addCharacter: (c) => {
      const id = genId();
      set((s) => {
        const characters = [...s.characters, { id, char: c }];
        return { characters, activeId: id, character: c };
      });
      return id;
    },

    removeCharacter: (id) => {
      set((s) => {
        const characters = s.characters.filter((e) => e.id !== id);
        let activeId = s.activeId;
        if (activeId === id) activeId = characters[0]?.id ?? null;
        return { characters, activeId, character: activeFrom(characters, activeId) };
      });
    },

    activateCharacter: (id) => {
      set((s) => {
        const entry = s.characters.find((e) => e.id === id);
        if (!entry) return s;
        return { ...s, activeId: id, character: entry.char };
      });
    },

    duplicateCharacter: (id) => {
      const src = get().characters.find((e) => e.id === id);
      if (!src) return null;
      const clone: Character = {
        ...(JSON.parse(JSON.stringify(src.char)) as Character),
        name: `${src.char.name || 'Unnamed'} (copy)`,
        updatedAt: new Date().toISOString(),
      };
      const newId = genId();
      set((s) => ({
        characters: [...s.characters, { id: newId, char: clone }],
        activeId: newId,
        character: clone,
      }));
      return newId;
    },

    reset: () => set({ characters: [], activeId: null, character: null }),
  }))
);

// Persist the library on any change.
useStore.subscribe(
  (s) => ({ characters: s.characters, activeId: s.activeId }),
  (lib) => scheduleLibrarySave(lib),
  {
    equalityFn: (a, b) => a.characters === b.characters && a.activeId === b.activeId,
  }
);
